import useFetch from "../../../hooks/useFetch";
export { useFetchPendingTasks, useAcknowledgeTask } from "../../Task/apis";

/**
 * Hook to fetch mentor dashboard profile section
 * Calls GET /dashboard/profile
 * Returns mentorProfileDetails, mentorCourses, banners
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchDashboardProfile = (options = {}) => {
  return useFetch("/dashboard/profile", {
    method: "get",
    ...options,
  });
};

/**
 * Hook to fetch upcoming sessions for today
 * Calls GET /dashboard/sessions
 * Returns upcomingSessions array
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchUpcomingSessions = (options = {}) => {
  return useFetch("/dashboard/upcoming-sessions", {
    method: "get",
    ...options,
  });
};

/**
 * Hook to fetch pending class feedbacks for inbox
 * Calls GET /glimpse/getClassData with { date, dateInterval: 3, sendCount: 1, status: "pending" }
 * Returns glimpseClassData array and totalPending count
 * Only fetches entries from the last 72 hours with pending status
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchPendingFeedbacks = (options = {}) => {
  return useFetch("/glimpse/getClassData", {
    method: "get",
    params: {dateInterval: 3, status: "pending" },
    ...options,
  });
};

/**
 * Hook to fetch pending loop requests for inbox
 * Calls GET /loop/mentor/dashboard with { active: 1 }
 * Returns response array and activeCount
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchPendingLoopRequests = (options = {}) => {
  return useFetch("/loop/mentor/dashboard", {
    method: "get",
    params: { active: 1 },
    ...options,
  });
};

/**
 * Hook to fetch mentor payout stats
 * Calls GET /mentorPayout/getStats with { month, year, mentorId, onlyEarnings }
 * Returns payout data including classesEarnings, demosEarnings, otherEarnings, etc.
 * @param {Object} params - { month, year, mentorId, onlyEarnings }
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchScheduleStats = (options = {}) => {
  return useFetch("/dashboard/schedule-stats", {
    method: "get",
    ...options,
  });
};

/**
 * Hook to fetch mentor payout stats
 * Calls GET /mentorPayout/getStats with { month, year, mentorId, onlyEarnings }
 * Returns payout data including classesEarnings, demosEarnings, otherEarnings, etc.
 * @param {Object} params - { month, year, mentorId }
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchMentorPayoutStats = (params, options = {}) => {
  const { enabled = true, ...restOptions } = options;
  const { month, year, mentorId } = params;

  return useFetch("/mentorPayout/getStats", {
    method: "get",
    params: { month, year, mentorId, onlyEarnings: 1 },
    enabled: !!mentorId && !!month && !!year && enabled,
    ...restOptions,
  });
};

/**
 * Hook to fetch mentor courses for demo
 * Calls GET /getMentorCourseForDemo with { mentorId }
 * Returns mentorCourses array and blacklisted flag
 * @param {Object} params - { mentorId }
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchMentorCourses = (params, options = {}) => {
  const { enabled = true, ...restOptions } = options;
  const { mentorId } = params;

  return useFetch("/getMentorCourseForDemo", {
    method: "get",
    params: { mentorId },
    enabled: !!mentorId && enabled,
    ...restOptions,
  });
};

/**
 * Hook to fetch all mentors list (admin only)
 * Calls GET /getAllMentors
 * Returns mentors array
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchAllMentors = (options = {}) => {
  return useFetch("/getAllMentors", {
    method: "get",
    ...options,
  });
};

/**
 * Hook to check mentor policy consent status
 * Calls GET /mentor/policyConsent with { mentorId, policyVersion }
 * Returns { alreadyAccepted: boolean }
 * @param {Object} params - { mentorId }
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchPolicyConsent = (params, options = {}) => {
  const { enabled = true, ...restOptions } = options;
  const { mentorId } = params;

  return useFetch("/mentor/policyConsent", {
    method: "get",
    params: { mentorId, policyVersion: "v2" },
    enabled: !!mentorId && enabled,
    ...restOptions,
  });
};

/**
 * Hook to submit mentor policy consent acceptance
 * Calls POST /mentor/policyConsent (manual: true — call mutate({ data }) to fire)
 * @param {Object} options - Options for the fetch (onSuccess, onError, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useSubmitPolicyConsent = (options = {}) => {
  return useFetch("/mentor/policyConsent", {
    method: "post",
    manual: true,
    ...options,
  });
};

/**
 * Hook to add mentor slot(s) — single or multiple date
 * Calls POST /addMentorSlot (manual: true — call mutate({ data }) to fire)
 * @param {Object} options - Options for the fetch (onSuccess, onError, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useAddMentorSlot = (options = {}) => {
  return useFetch("/addMentorSlot", {
    method: "post",
    manual: true,
    ...options,
  });
};

/**
 * Hook to manage auto-add slot actions (enable, disable, edit)
 * Calls POST /mentorSlot/autoAdd (manual: true — call mutate({ data }) to fire)
 * Payload: { timingList, action: "enable"|"disable", opted }
 * @param {Object} options - Options for the fetch (onSuccess, onError, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useAutoAddSlotAction = (options = {}) => {
  return useFetch("/mentorSlot/autoAdd", {
    method: "post",
    manual: true,
    ...options,
  });
};

/**
 * Hook to create a glimpse backfill entry for a session
 * Calls POST /glimpse/backfill/single (manual: true — call mutate({ data }) to fire)
 * Payload: { scheduleId, isManual: 1 }
 * @param {Object} options - Options for the fetch (onSuccess, onError, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useGlimpseBackfill = (options = {}) => {
  return useFetch("/glimpse/backfill/single", {
    method: "post",
    manual: true,
    ...options,
  });
};

/**
 * Hook to cancel a mentor request
 * Calls PUT /mentor/cancel/:reqId (manual: true — call mutate({ url, data }) to fire)
 * @param {Object} options - Options for the fetch (onSuccess, onError, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useCancelMentorRequest = (options = {}) => {
  return useFetch("/mentor/cancel", {
    method: "put",
    manual: true,
    ...options,
  });
};

/**
 * Hook to approve/decline a loop request
 * Calls POST /loop/mentor (manual: true — call mutate({ data }) to fire)
 * Payload: { loopReqId, loopUserReqId, requestType, accepted: 0|1 }
 * @param {Object} options - Options for the fetch (onSuccess, onError, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useLoopMentorAction = (options = {}) => {
  return useFetch("/loop/mentor", {
    method: "post",
    manual: true,
    ...options,
  });
};

/**
 * Hook to expire/delete a loop admin request
 * Calls DELETE /loop/admin/expire (manual: true — call mutate({ data }) to fire)
 * Payload: { loopUserReqId }
 * @param {Object} options - Options for the fetch (onSuccess, onError, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useLoopAdminExpire = (options = {}) => {
  return useFetch("/loop/admin/expire", {
    method: "delete",
    manual: true,
    ...options,
  });
};

/**
 * Hook to fetch student stats for the My Students section
 * Calls GET /dashboard/student-stats
 * Returns studentStats array with per-student data (classes, homework, AI homework, goals, streaks)
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchStudentStats = (options = {}) => {
  return useFetch("/dashboard/student-stats", {
    method: "get",
    ...options,
  });
};

/**
 * Hook to fetch a student's pending AI homework for the popup on the My Students card
 * Calls GET /dashboard/homework/ai-pending/:studentId with optional { batchId }
 * Returns { count, returned, hasMore, pendingAiHomework }
 * @param {Object} params - { studentId, batchId }
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchPendingAiHomework = ({ studentId, batchId } = {}, options = {}) => {
  return useFetch(studentId ? `/dashboard/homework/ai-pending/${encodeURIComponent(studentId)}` : "", {
    method: "get",
    params: batchId ? { batchId } : {},
    ...options,
    enabled: Boolean(studentId) && options?.enabled !== false,
  });
};

/**
 * Hook to fetch the AI learning summary (student brief) for a batch
 * Calls GET /epicenter/student-summary with { batchId }
 * Returns { studentId, studentName, summaryCards, homeworkPerformance }
 * @param {Object} params - { batchId }
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchStudentAiBrief = (params, options = {}) => {
  const { enabled = true, ...restOptions } = options;
  const { batchId } = params;

  return useFetch("/epicenter/student-summary", {
    method: "get",
    params: { batchId },
    enabled: !!batchId && enabled,
    ...restOptions,
  });
};

/**
 * Hook to report an issue on behalf of a student
 * Calls POST /crm/ticket (manual: true — call mutate({ data }) to fire)
 * Payload: { accountId, subject, description, priority, additionalFields }
 * @param {Object} options - Options for the fetch (onSuccess, onError, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useReportIssue = (options = {}) => {
  return useFetch("/crm/ticket", {
    method: "post",
    manual: true,
    ...options,
  });
};

/**
 * Hook to search exercises for homework assignment
 * Calls GET /glimpse/searchExercisesOrTopics (manual: true)
 * Pass { params: { text, match } } to mutate — match is "exercise" or "exerciseUrl"
 * @param {Object} options - Options for the fetch (onSuccess, onError, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useSearchExercises = (options = {}) => {
  return useFetch("/glimpse/searchExercisesOrTopics", {
    method: "get",
    manual: true,
    ...options,
  });
};

/**
 * Hook to assign homework to a student from the mentor dashboard
 * Calls POST /dashboard/homework/assign (manual: true — call mutate({ data }) to fire)
 * Supports multipart/form-data for file uploads
 * @param {Object} options - Options for the fetch (onSuccess, onError, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useAssignHomework = (options = {}) => {
  return useFetch("/dashboard/homework/assign", {
    method: "post",
    manual: true,
    ...options,
  });
};

/**
 * Hook to fetch all homework assigned to a student (from glimpse + dashboard)
 * Calls GET /dashboard/homework/review/:studentId (manual: true)
 * Call mutate({ url: `/dashboard/homework/review/${studentId}` }) to fire
 * @param {Object} options - Options for the fetch (onSuccess, onError, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchReviewHomework = (options = {}) => {
  return useFetch("/dashboard/homework/review", {
    method: "get",
    manual: true,
    ...options,
  });
};

/**
 * Hook to delete all recurring schedules for a mentor
 * Calls DELETE /recurring (manual: true — call mutate({ data }) to fire)
 * Payload: { freeSlots: [], deleteAll: true }
 * @param {Object} options - Options for the fetch (onSuccess, onError, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useDeleteRecurring = (options = {}) => {
  return useFetch("/recurring", {
    method: "delete",
    manual: true,
    ...options,
  });
};

/**
 * Hook to create recurring schedules for a mentor
 * Calls POST /recurring (manual: true — call mutate({ data }) to fire)
 * Payload: { timings: [{ dayIndex, start, end }] }
 * @param {Object} options - Options for the fetch (onSuccess, onError, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const usePostRecurring = (options = {}) => {
  return useFetch("/recurring", {
    method: "post",
    manual: true,
    ...options,
  });
};

/**
 * Hook to assign AI test(s) to a student
 * Calls POST /mentor/tests (manual: true — call mutate({ data }) to fire)
 * Payload: { studentIds: [], subject, tests: [{ testType, topics? , grade? }] }
 * Returns: { success, msg }
 * @param {Object} options - Options for the fetch (onSuccess, onError, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useAssignAiTest = (options = {}) => {
  return useFetch("/mentor/tests", {
    method: "post",
    manual: true,
    ...options,
  });
};

/**
 * Hook to fetch AI-recommended (already assigned) test topics for a student
 * Calls GET /student/assignedTopics with { studentId, subject, grade }
 * Returns: { topics: [{ topicId, topicName }] }
 * @param {Object} params - { studentId, subject, grade }
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchAssignedTopics = (params = {}, options = {}) => {
  const { enabled = true, ...restOptions } = options;
  const { studentId, subject, grade } = params;

  return useFetch("/student/assignedTopics", {
    method: "get",
    params: { studentId, subject, grade },
    enabled: enabled && !!studentId && !!subject,
    ...restOptions,
  });
};

/**
 * Hook to fetch grade/levels for a subject (used by the Grade test popup)
 * Calls GET /api/goal/levels?type=<subject>
 * Returns: { level: [{ value, label }] }
 * @param {string} subject - the class subject / courseSection slug
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchGradeLevels = (subject, options = {}) => {
  const { enabled = true, ...restOptions } = options;

  return useFetch("/api/goal/levels", {
    method: "get",
    params: { type: subject },
    enabled: enabled && !!subject,
    ...restOptions,
  });
};
